import { supabase } from '@/lib/supabaseClient';
import { WeightCheckRecord } from '@/types/database';

export interface LogWeightCheckPayload {
  cycle_id: string;
  check_date: string;
  sample_head_count: number;
  sample_total_weight_kg: number;
  production_unit_id?: string | null;
  notes?: string | null;
}

export const weightCheckService = {
  /**
   * Fetches all non-void weight checks for a given cycle.
   */
  async getWeightChecksByCycle(cycleId: string): Promise<WeightCheckRecord[]> {
    const { data, error } = await supabase
      .from('weight_checks')
      .select(`
        *,
        production_units (id, unit_code, unit_name)
      `)
      .eq('cycle_id', cycleId)
      .eq('is_void', false)
      .order('check_date', { ascending: false });

    if (error) {
      console.error('[weightCheckService.getWeightChecksByCycle] Error:', error.message);
      throw error;
    }

    return (data || []) as WeightCheckRecord[];
  },

  /**
   * Logs a new weight check event with verified physical ADG calculation.
   * ADG is calculated ONLY when sufficient compatible measured physical data exists
   * (unambiguous single arrival batch with measured placement weight, days_on_feed > 0, no transfers).
   * Otherwise sets calculated_adg_kg to null.
   */
  async logWeightCheck(payload: LogWeightCheckPayload): Promise<WeightCheckRecord> {
    if (!payload.cycle_id) {
      throw new Error('Cycle ID is required.');
    }
    if (!payload.check_date) {
      throw new Error('Check date is required.');
    }
    if (!payload.sample_head_count || payload.sample_head_count <= 0) {
      throw new Error('Sample head count must be greater than zero.');
    }
    if (!payload.sample_total_weight_kg || payload.sample_total_weight_kg <= 0) {
      throw new Error('Sample total weight (kg) must be greater than zero.');
    }

    // Fetch cycle info to compute days on feed and check arrival weights
    const [cycleRes, arrivalsRes, transfersRes] = await Promise.all([
      supabase
        .from('production_cycles')
        .select('id, start_date')
        .eq('id', payload.cycle_id)
        .single(),

      supabase
        .from('pig_arrivals')
        .select('id, head_count, total_weight_kg, is_void')
        .eq('cycle_id', payload.cycle_id)
        .eq('is_void', false),

      supabase
        .from('pig_transfers')
        .select('id, head_count, is_void')
        .or(`source_cycle_id.eq.${payload.cycle_id},destination_cycle_id.eq.${payload.cycle_id}`)
        .eq('is_void', false),
    ]);

    let daysOnFeed: number | null = null;
    let calculatedAdg: number | null = null;

    if (cycleRes.data?.start_date) {
      const startDate = new Date(cycleRes.data.start_date);
      const checkDate = new Date(payload.check_date);
      const diffTime = checkDate.getTime() - startDate.getTime();
      daysOnFeed = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    }

    // Check if sufficient measured physical data exists for ADG
    const arrivals = arrivalsRes.data || [];
    const transfers = transfersRes.data || [];

    const avgSampleWeight = payload.sample_total_weight_kg / payload.sample_head_count;

    // Strict condition: exactly 1 arrival, no transfers, daysOnFeed > 0, arrival has valid weight
    if (
      arrivals.length === 1 &&
      transfers.length === 0 &&
      daysOnFeed !== null &&
      daysOnFeed > 0 &&
      arrivals[0].head_count > 0 &&
      arrivals[0].total_weight_kg > 0
    ) {
      const avgIntakeWeight = Number(arrivals[0].total_weight_kg) / Number(arrivals[0].head_count);
      const gainPerPig = avgSampleWeight - avgIntakeWeight;
      if (gainPerPig >= 0) {
        calculatedAdg = Number((gainPerPig / daysOnFeed).toFixed(4));
      }
    }

    const insertData: any = {
      cycle_id: payload.cycle_id,
      check_date: payload.check_date,
      sample_head_count: payload.sample_head_count,
      sample_total_weight_kg: payload.sample_total_weight_kg,
      days_on_feed: daysOnFeed,
      calculated_adg_kg: calculatedAdg,
      production_unit_id: payload.production_unit_id || null,
      notes: payload.notes?.trim() || null,
    };

    const { data, error } = await supabase
      .from('weight_checks')
      .insert(insertData)
      .select('*')
      .single();

    if (error || !data) {
      console.error('[weightCheckService.logWeightCheck] Insert error:', error?.message);
      throw new Error(`Failed to log weight check: ${error?.message}`);
    }

    return data as WeightCheckRecord;
  },
};
